import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import {
  ArrowLeft, Users, Plus, Trash2, BookOpen, Zap, Target,
  Flame, UserPlus, ChevronRight, GraduationCap, Mail,
  CheckCircle, Award, BarChart3, FileText, Copy, Printer
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";

export default function EducatorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [apiMaxStudents, setApiMaxStudents] = useState(4);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ student_email: "", student_name: "" });
  const [inviting, setInviting] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchData = async () => {
    try {
      const [studentsRes, dashRes] = await Promise.all([
        api.get("/educator/students"),
        api.get("/educator/dashboard")
      ]);
      setStudents(studentsRes.data.students || []);
      setApiMaxStudents(studentsRes.data.max_students || 4);
      setDashboard(dashRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Educator tier required");
        navigate("/pricing");
      }
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const inviteStudent = async () => {
    if (!inviteForm.student_email || !inviteForm.student_name) {
      toast.error("Enter student name and email");
      return;
    }
    setInviting(true);
    try {
      await api.post("/educator/students/invite", inviteForm);
      toast.success(`${inviteForm.student_name} has been invited!`);
      setInviteForm({ student_email: "", student_name: "" });
      setShowInvite(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to invite student");
    }
    setInviting(false);
  };

  const removeStudent = async (id, name) => {
    if (!window.confirm(`Remove ${name}? Their access will be revoked.`)) return;
    try {
      await api.delete(`/educator/students/${id}`);
      toast.success(`${name} removed`);
      fetchData();
    } catch {
      toast.error("Failed to remove student");
    }
  };

  const maxStudents = apiMaxStudents;
  const isUnlimited = maxStudents >= 999;
  const slotsUsed = students.length;
  const slotsRemaining = isUnlimited ? 999 : Math.max(0, maxStudents - slotsUsed);
  const activeStudents = students.filter(s => s.progress?.registered);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "students", label: "Students", icon: Users, count: slotsUsed },
    { id: "invite", label: "Add Student", icon: UserPlus },
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="educator-dashboard-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white" data-testid="edu-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">
            <GraduationCap className="inline mr-2 text-[#F7931A]" size={20} />
            EDUCATOR <span className="text-[#F7931A]">DASHBOARD</span>
          </h1>
          <div className="w-5" />
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="sticky top-[65px] z-40 glass-darker border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2" data-testid="educator-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === "invite") setShowInvite(true);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-[#F7931A]/15 text-[#F7931A] border border-[#F7931A]/30"
                    : "text-zinc-500 hover:text-zinc-300 hover:bg-white/5"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={16} />
                {tab.label}
                {tab.count !== undefined && (
                  <span className="bg-[#F7931A]/20 text-[#F7931A] text-xs px-1.5 py-0.5 rounded font-mono">
                    {tab.count !== undefined ? `${tab.count}${tab.id === "students" && !isUnlimited ? `/${maxStudents}` : ""}` : ""}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* ═══ OVERVIEW TAB ═══ */}
        {activeTab === "overview" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Welcome / Getting Started */}
            {slotsUsed === 0 ? (
              <div className="card-dark p-8 text-center border-dashed border-[#F7931A]/30" data-testid="edu-welcome">
                <GraduationCap className="text-[#F7931A] mx-auto mb-4" size={48} />
                <h2 className="font-heading text-2xl font-bold text-white mb-2">Welcome, Educator</h2>
                <p className="text-zinc-400 text-sm max-w-md mx-auto mb-6">
                  You have {isUnlimited ? "unlimited" : maxStudents} student seats included with your Educator License.
                  Add your first student to get started — they'll get full Bitcoin Brain access for free.
                </p>
                <Button onClick={() => { setActiveTab("invite"); setShowInvite(true); }} className="btn-primary" data-testid="edu-get-started-btn">
                  <UserPlus size={18} className="mr-2" /> Add Your First Student
                </Button>
              </div>
            ) : (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="edu-stats-grid">
                  <div className="card-dark p-4">
                    <p className="text-zinc-500 text-xs mb-1">Student Seats</p>
                    <p className="text-white font-mono font-bold text-2xl" data-testid="student-count">
                      {slotsUsed}{!isUnlimited && <span className="text-zinc-600 text-sm">/{maxStudents}</span>}
                    </p>
                    <p className="text-zinc-600 text-[10px] mt-0.5">{isUnlimited ? "Unlimited seats" : `${slotsRemaining} seat${slotsRemaining !== 1 ? "s" : ""} available`}</p>
                  </div>
                  <div className="card-dark p-4">
                    <p className="text-zinc-500 text-xs mb-1">Active Learners</p>
                    <p className="text-green-400 font-mono font-bold text-2xl" data-testid="active-count">{dashboard?.active_students || 0}</p>
                    <p className="text-zinc-600 text-[10px] mt-0.5">Registered & learning</p>
                  </div>
                  <div className="card-dark p-4">
                    <p className="text-zinc-500 text-xs mb-1">Combined BTC XP</p>
                    <p className="text-[#F7931A] font-mono font-bold text-2xl">{(dashboard?.total_student_xp || 0).toLocaleString()}</p>
                    <p className="text-zinc-600 text-[10px] mt-0.5">All students</p>
                  </div>
                  <div className="card-dark p-4">
                    <p className="text-zinc-500 text-xs mb-1">Avg Completion</p>
                    <p className="text-white font-mono font-bold text-2xl">{dashboard?.completion_rate || 0}%</p>
                    <p className="text-zinc-600 text-[10px] mt-0.5">{dashboard?.avg_sections_per_student || 0} / 21 sections</p>
                  </div>
                </div>

                {/* Student Overview Cards */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-lg font-bold text-white">Student Progress</h3>
                    <button onClick={() => setActiveTab("students")} className="text-[#F7931A] text-sm hover:underline flex items-center gap-1" data-testid="view-all-students-btn">
                      View all <ChevronRight size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {students.slice(0, 4).map((s, i) => (
                      <StudentMiniCard key={s.id} student={s} index={i} />
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div>
                  <h3 className="font-heading text-lg font-bold text-white mb-3">Quick Actions</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {slotsRemaining > 0 && (
                      <button
                        onClick={() => { setActiveTab("invite"); setShowInvite(true); }}
                        className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-[#F7931A]/50 transition-all group text-center"
                        data-testid="quick-add-student"
                      >
                        <UserPlus className="text-[#F7931A] mb-2 mx-auto" size={24} />
                        <p className="text-white text-sm group-hover:text-[#F7931A]">Add Student</p>
                        <p className="text-zinc-600 text-[10px]">{slotsRemaining} seat{slotsRemaining !== 1 ? "s" : ""} left</p>
                      </button>
                    )}
                    <button
                      onClick={() => setActiveTab("students")}
                      className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-[#F7931A]/50 transition-all group text-center"
                      data-testid="quick-manage-students"
                    >
                      <Users className="text-[#F7931A] mb-2 mx-auto" size={24} />
                      <p className="text-white text-sm group-hover:text-[#F7931A]">Manage Students</p>
                      <p className="text-zinc-600 text-[10px]">{slotsUsed} linked</p>
                    </button>
                    <button
                      onClick={() => navigate("/course")}
                      className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-[#F7931A]/50 transition-all group text-center"
                      data-testid="quick-view-course"
                    >
                      <BookOpen className="text-[#F7931A] mb-2 mx-auto" size={24} />
                      <p className="text-white text-sm group-hover:text-[#F7931A]">View Course</p>
                      <p className="text-zinc-600 text-[10px]">21 sections</p>
                    </button>
                    <button
                      onClick={() => navigate("/kid-mode")}
                      className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-[#00E5FF]/50 transition-all group text-center"
                      data-testid="quick-kid-mode"
                    >
                      <Target className="text-[#00E5FF] mb-2 mx-auto" size={24} />
                      <p className="text-white text-sm group-hover:text-[#00E5FF]">Kid Mode</p>
                      <p className="text-zinc-600 text-[10px]">Budget sim & games</p>
                    </button>
                    <button
                      onClick={() => navigate("/educator/progress-report")}
                      className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-[#F7931A]/50 transition-all group text-center"
                      data-testid="quick-progress-report"
                    >
                      <BarChart3 className="text-[#F7931A] mb-2 mx-auto" size={24} />
                      <p className="text-white text-sm group-hover:text-[#F7931A]">Progress Report</p>
                      <p className="text-zinc-600 text-[10px]">Printable PDF</p>
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* ═══ STUDENTS TAB ═══ */}
        {activeTab === "students" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-bold text-white">Linked Students</h3>
                <p className="text-zinc-500 text-xs">{slotsUsed} {isUnlimited ? "students linked — Unlimited seats" : `of ${maxStudents} seats used — ${slotsRemaining} available`}</p>
              </div>
              {slotsRemaining > 0 && (
                <Button onClick={() => { setActiveTab("invite"); setShowInvite(true); }} size="sm" className="btn-primary" data-testid="invite-student-btn">
                  <UserPlus size={16} className="mr-1" /> Add Student
                </Button>
              )}
            </div>

            {/* Seat indicators */}
            {!isUnlimited && (
            <div className="flex gap-2" data-testid="seat-indicators">
              {Array.from({ length: maxStudents }).map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 h-2 rounded-full ${
                    i < slotsUsed ? "bg-[#F7931A]" : "bg-zinc-800 border border-white/5"
                  }`}
                />
              ))}
            </div>
            )}

            {students.length === 0 ? (
              <div className="card-dark p-8 text-center" data-testid="no-students">
                <Users className="text-zinc-700 mx-auto mb-3" size={40} />
                <p className="text-zinc-400 text-sm mb-1">No students linked yet</p>
                <p className="text-zinc-600 text-xs">Add a student by name and email. If they already have an account, they get instant access.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {students.map((s, i) => (
                  <StudentFullCard key={s.id} student={s} index={i} onRemove={removeStudent} />
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ═══ INVITE TAB ═══ */}
        {activeTab === "invite" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {slotsRemaining <= 0 ? (
              <div className="card-dark p-8 text-center" data-testid="seats-full">
                <Users className="text-zinc-500 mx-auto mb-3" size={40} />
                <h3 className="text-white font-heading font-bold text-lg mb-2">All Seats Filled</h3>
                <p className="text-zinc-500 text-sm mb-4">
                  You've linked {maxStudents} students — the maximum for your Educator License.
                  Remove a student to free up a seat.
                </p>
                <Button onClick={() => setActiveTab("students")} variant="outline" className="border-[#F7931A]/30 text-[#F7931A]">
                  Manage Students
                </Button>
              </div>
            ) : (
              <div className="max-w-lg mx-auto">
                <div className="text-center mb-6">
                  <UserPlus className="text-[#F7931A] mx-auto mb-3" size={36} />
                  <h3 className="font-heading text-xl font-bold text-white mb-1">Add a Student</h3>
                  <p className="text-zinc-500 text-sm">
                    {isUnlimited ? "Unlimited seats available" : `${slotsRemaining} of ${maxStudents} seats available`}. Students get full Bitcoin Brain access for free.
                  </p>
                </div>

                <div className="card-dark p-6 space-y-4" data-testid="invite-form">
                  <div>
                    <label className="text-zinc-400 text-xs block mb-1.5">Student or Child's Name</label>
                    <input
                      type="text"
                      value={inviteForm.student_name}
                      onChange={e => setInviteForm({ ...inviteForm, student_name: e.target.value })}
                      placeholder="e.g. Alex Fields"
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#F7931A]/50 focus:outline-none"
                      data-testid="student-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs block mb-1.5">Their Email Address</label>
                    <input
                      type="email"
                      value={inviteForm.student_email}
                      onChange={e => setInviteForm({ ...inviteForm, student_email: e.target.value })}
                      placeholder="e.g. alex@example.com"
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#F7931A]/50 focus:outline-none"
                      data-testid="student-email-input"
                    />
                    <p className="text-zinc-600 text-xs mt-1.5">
                      If they already have a Bitcoin Brain account, they'll get instant access. Otherwise, they'll get access when they sign up with this email.
                    </p>
                  </div>

                  <Button onClick={inviteStudent} disabled={inviting} className="w-full btn-primary min-h-[48px]" data-testid="confirm-invite-btn">
                    {inviting ? "Adding..." : "Add Student"}
                  </Button>
                </div>

                {/* How it works */}
                <div className="mt-6 space-y-3" data-testid="how-it-works">
                  <p className="text-zinc-500 text-xs font-medium">How it works</p>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#F7931A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#F7931A] text-xs font-bold">1</span>
                    </div>
                    <p className="text-zinc-400 text-sm">Enter your student or child's name and email address</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#F7931A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#F7931A] text-xs font-bold">2</span>
                    </div>
                    <p className="text-zinc-400 text-sm">They sign up at bitcoinbrain.space with that email — full access is automatic</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#F7931A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#F7931A] text-xs font-bold">3</span>
                    </div>
                    <p className="text-zinc-400 text-sm">Track their progress right here — sections completed, BTC XP earned, badges</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
}


/* ─── Student Mini Card (Overview Tab) ─── */
function StudentMiniCard({ student, index }) {
  const s = student;
  const registered = s.progress?.registered;
  const sections = s.progress?.sections_completed || 0;
  const pct = Math.round((sections / 21) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="card-dark p-4 flex items-center gap-3"
      data-testid={`student-mini-${index}`}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
        registered ? "bg-[#F7931A]/20 text-[#F7931A]" : "bg-zinc-800 text-zinc-500"
      }`}>
        {s.student_name?.[0]?.toUpperCase() || "?"}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{s.student_name}</p>
        {registered ? (
          <div className="flex items-center gap-2 mt-1">
            <Progress value={pct} className="h-1 flex-1" />
            <span className="text-zinc-500 text-[10px] font-mono">{sections}/21</span>
          </div>
        ) : (
          <p className="text-yellow-500/70 text-xs">Invited — waiting to sign up</p>
        )}
      </div>
      {registered && (
        <span className="text-[#F7931A] font-mono text-xs">{(s.progress?.total_xp || 0).toLocaleString()} BTC XP</span>
      )}
    </motion.div>
  );
}


/* ─── Student Full Card (Students Tab) ─── */
function StudentFullCard({ student, index, onRemove }) {
  const navigate = useNavigate();
  const s = student;
  const registered = s.progress?.registered;
  const sections = s.progress?.sections_completed || 0;
  const pct = Math.round((sections / 21) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="card-dark p-5"
      data-testid={`student-card-${index}`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
            registered ? "bg-[#F7931A]/20 text-[#F7931A]" : "bg-zinc-800 text-zinc-500"
          }`}>
            {s.student_name?.[0]?.toUpperCase() || "?"}
          </div>
          <div>
            <p className="text-white font-medium">{s.student_name}</p>
            <p className="text-zinc-500 text-xs flex items-center gap-1">
              <Mail size={12} /> {s.student_email}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded ${
            registered ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"
          }`}>
            {registered ? "Active" : "Invited"}
          </span>
          <button
            onClick={() => onRemove(s.id, s.student_name)}
            className="text-zinc-700 hover:text-red-400 transition-colors p-1"
            data-testid={`remove-student-${index}`}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Progress section */}
      {registered ? (
        <>
          {/* Course progress bar */}
          <div className="mb-3">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-zinc-500">Course Progress</span>
              <span className="text-[#F7931A] font-mono">{pct}%</span>
            </div>
            <Progress value={pct} className="h-2" />
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-4 gap-2">
            <div className="text-center p-2 bg-[#0A0A0A] rounded-lg">
              <BookOpen className="text-[#F7931A] mx-auto mb-1" size={16} />
              <p className="text-white font-mono text-sm font-bold">{sections}/21</p>
              <p className="text-zinc-600 text-[10px]">Sections</p>
            </div>
            <div className="text-center p-2 bg-[#0A0A0A] rounded-lg">
              <Zap className="text-[#F7931A] mx-auto mb-1" size={16} />
              <p className="text-white font-mono text-sm font-bold">{(s.progress?.total_xp || 0).toLocaleString()}</p>
              <p className="text-zinc-600 text-[10px]">BTC XP</p>
            </div>
            <div className="text-center p-2 bg-[#0A0A0A] rounded-lg">
              <Award className="text-[#F7931A] mx-auto mb-1" size={16} />
              <p className="text-white font-mono text-sm font-bold">{s.progress?.badges_count || 0}</p>
              <p className="text-zinc-600 text-[10px]">Badges</p>
            </div>
            <div className="text-center p-2 bg-[#0A0A0A] rounded-lg">
              <Flame className="text-[#F7931A] mx-auto mb-1" size={16} />
              <p className="text-white font-mono text-sm font-bold">{s.progress?.streak || 0}</p>
              <p className="text-zinc-600 text-[10px]">Streak</p>
            </div>
          </div>

          {/* Print certificate button */}
          {sections > 0 && (
            <button
              onClick={() => navigate(`/educator/certificate/${s.id}`)}
              className="w-full mt-3 flex items-center justify-center gap-2 p-2 bg-[#F7931A]/10 border border-[#F7931A]/20 rounded-lg text-[#F7931A] text-xs hover:bg-[#F7931A]/20 transition-colors"
              data-testid={`print-cert-${index}`}
            >
              <Printer size={14} /> Print Certificate
            </button>
          )}
        </>
      ) : (
        <div className="bg-[#0A0A0A] rounded-lg p-3 text-center">
          <p className="text-zinc-500 text-xs">Waiting for {s.student_name} to create an account at bitcoinbrain.space</p>
          <p className="text-zinc-600 text-[10px] mt-1">They need to sign up using: {s.student_email}</p>
        </div>
      )}
    </motion.div>
  );
}
